import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Github, House } from "lucide-react";
import { GitHubOrgSelector } from "../GitHubOrgSelector";
import { BlacksmithLogomarkSVG, BlacksmithWordmarkSVG } from "../BlacksmithLogoSVG";
import { CodesmithLottieIcon } from "@/components/icons/CodesmithLottieIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useHasHoverPointer, useIsCompactViewport } from "@/hooks/use-device";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@repo/blacksmith-ui/cn";
import { AppHeaderBar } from "@/components/ui/app-header";
import { CollapsedIconTooltip, RAIL_REDESIGN_CLASS, RAIL_ROW, RAIL_ROW_ACTIVE, RailRowContents, SidebarAdminRow, SidebarGlobalFooter } from "./SidebarGlobalChrome";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { SidebarBlockedOrgBanner, useSidebarGlobalBehaviors } from "./useSidebarGlobalBehaviors";
import { useActiveApp, isHomePath, isStandaloneAccountPath, isAdminPath, settingsPagePath } from "./useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "./useCodesmithEnabledCache";
import { RAIL_WIDTH, RAIL_REDESIGN_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_WIDTH_CSS_VAR, RAIL_WIDTH_TRANSITION_MS, RAIL_TRANSITION_EASING } from "./railConstants";
import { RAIL_SHORTCUT_LABELS, railShortcutSection, type RailShortcutSection } from "./railShortcuts";

/**
 * Slim app-switcher rail pinned to the left edge of the app. Hosts the
 * Blacksmith logo, top-level app entries (Home, GitHub Actions, Codesmith,
 * Proof), and the global footer controls (support, settings, notifications,
 * org switcher).
 *
 * The rail is 48px wide by default; clicking the logo pins it open at 200px
 * (row labels, org selector, full wordmark) until the logo is clicked again —
 * it does not auto-collapse on hover-out or navigation. The expanded rail is
 * part of the page layout: the contextual sidebar and main content slide
 * over in lockstep with the panel's own width transition (see `layoutWidth`
 * below and the matching `left` transition on the contextual Sidebar), rather
 * than being covered by an overlay.
 */

// Master switch for the rail's whole-panel hover expansion. Off by default:
// the rail stays 48px and each row surfaces its own tooltip, while the
// vCPU/status widget expands as a standalone hover overlay (RailStatusHoverCard)
// rather than growing the whole rail. Flip to true to restore the original
// behavior where hovering expands the entire rail to reveal row labels. Typed
// as boolean so both code paths stay type-checked at the default value.
const RAIL_HOVER_EXPAND: boolean = false;

// Hover-intent dwell before expanding. The pointer must rest on the rail for
// this long before the wipe starts, so a quick pass-over (e.g. on the way to
// the main content) never kicks off a half-open-then-reverse animation. Close
// stays instant, so the rail still feels snappy.
const RAIL_OPEN_DELAY_MS = 200;

// The mutually-exclusive rail rows that can read as active.
type RailSection = "home" | "actions" | "codesmith" | "proof" | "usage" | "admin";

// Slide/wipe transition for the expand/collapse. easeOutQuint (see
// RAIL_TRANSITION_EASING): the panel starts moving immediately and
// decelerates smoothly into its final width (an ease-out reads far smoother
// here than an ease-in-out, which hesitates then snaps). Duration/easing are
// shared with the contextual Sidebar's `left` transition (railConstants.ts)
// so the panel, the pushed main content, and the fixed-position contextual
// sidebar all slide in lockstep rather than one visibly lagging the others.
//
// This is an inline style, not a Tailwind class: Tailwind's content scanner
// only recognizes class strings that appear literally in source, and this
// value is assembled from imported constants, so a `[transition:...]`
// arbitrary-value class built from it would never get a matching CSS rule
// generated — the rail would silently skip the transition and jump straight
// to its new width. That's invisible on pages with a contextual Sidebar
// (its own `left` transition carries the visible slide), but on Codesmith
// pages, which push a plain flex sibling (SessionSidebar) with nothing else
// animating, it read as no transition at all.
const RAIL_TRANSITION_STYLE = `width ${RAIL_WIDTH_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`;

// Set on navigation; hover expansion is ignored while true so the panel does
// not pop back open over the section the user just clicked into. Cleared once
// the pointer leaves the rail (a fresh hover re-enables expansion). Lives at
// module scope because the rail can still unmount and remount within a session
// (its Suspense boundary in SidebarLayout) and the flag must survive that
// remount.
let suppressExpandUntilPointerLeaves = false;

// Clears the post-navigation suppression once the pointer is actually off the
// rail. React's onMouseLeave can't be relied on for this: clicking a row
// collapses the panel (200→48px) instantly under a stationary cursor, and a
// resize out from under the pointer doesn't dispatch mouseleave — so the flag
// would otherwise stay armed and swallow the next genuine hover. A
// document-level mousemove checks the live pointer position instead.
//
// The listener lives at module scope, like the flag it clears: it must stay
// attached across any rail remount, because a mousemove missed in an
// instance-bound listener's teardown/re-arm gap could let the
// pointer travel off the rail unseen and then re-enter with the first
// delivered event already targeting the panel — leaving the flag stuck and
// hover dead until the pointer exits and re-enters. The rail is resolved from
// the DOM at event time (live rail or its suspense skeleton), so the check
// works whichever instance is mounted. If the rail leaves the page entirely,
// the first mousemove finds no rail, clears the flag, and removes the
// listener, so nothing lingers.
let suppressClearListener: ((ev: MouseEvent) => void) | null = null;
function removeSuppressClearListener() {
  if (suppressClearListener) {
    document.removeEventListener("mousemove", suppressClearListener);
    suppressClearListener = null;
  }
}

// Persists the logo-click pin (see `expanded`) across that same remount.
// Read once as the initial state on mount and written back on every change,
// so when the rail comes back after unmounting it resumes exactly how it was
// left, rather than resetting to collapsed each time.
let pinnedExpanded = false;

// `timeStamp` of the last keydown into a text field (see railShortcutSection).
// Module scope, like pinnedExpanded, so it survives rail remounts.
let lastTextEntryAt = -Infinity;
function armSuppressUntilPointerLeaves() {
  suppressExpandUntilPointerLeaves = true;
  removeSuppressClearListener();
  const check = (ev: MouseEvent) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('[data-slot="app-switcher-rail"], [data-slot="app-switcher-rail-skeleton"]')) {
      return;
    }
    suppressExpandUntilPointerLeaves = false;
    removeSuppressClearListener();
  };
  suppressClearListener = check;
  document.addEventListener("mousemove", check);
}
export default function AppSwitcherRail() {
  const $ = _c(169);
  const router = useRouter();
  const activeApp = useActiveApp();
  const routeSection = isAdminPath(router.pathname) ? "admin" : router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isHomePath(router.pathname) ? "home" : isStandaloneAccountPath(router.pathname) ? null : activeApp === "codesmith" ? "codesmith" : activeApp === "proof" ? "proof" : "actions";
  const [optimisticSection, setOptimisticSection] = useState(null);
  const activeSection = optimisticSection ?? routeSection;
  const railRowNavigationRef = useRef(false);
  useIsCompactViewport();
  const hasHoverPointer = useHasHoverPointer();
  const {
    activeGithubOrg,
    codesmithAutomationsEnabled,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  const urlOrg = router.query.org;
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== orgsResolved || $[2] !== urlOrg) {
    t0 = (typeof urlOrg === "string" ? urlOrg : undefined) || activeGithubOrg || (orgsResolved ? undefined : getCachedLastActiveOrg());
    $[0] = activeGithubOrg;
    $[1] = orgsResolved;
    $[2] = urlOrg;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const currentOrg = t0;
  const homeHref = currentOrg ? `/${currentOrg}/home` : "/home";
  const runsJobsHref = currentOrg ? `/${currentOrg}/runs/jobs` : "/runs";
  const sidebarVariant = useCodesmithSidebarVariant(currentOrg ?? null);
  const redesign = sidebarVariant === "redesign";
  const sessionsNewHref = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
  const proofHref = currentOrg ? `/${currentOrg}/proof` : "/proof";
  const {
    activeGithubOrgBlocked: globalOrgBlocked,
    gPrefixActiveRef
  } = useSidebarGlobalBehaviors();
  const [expanded, setExpanded] = useState(pinnedExpanded);
  const slim = !expanded;
  const layoutWidth = expanded ? RAIL_EXPANDED_WIDTH : redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH;
  const [instantClose, setInstantClose] = useState(false);
  const [codesmithRowHovered, setCodesmithRowHovered] = useState(false);
  const panelRef = useRef(null);
  const openTimerRef = useRef(null);
  const portalMouseListenerRef = useRef(null);
  const portalObserverRef = useRef(null);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const clearOpenTimer = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (portalMouseListenerRef.current) {
        document.removeEventListener("mousemove", portalMouseListenerRef.current);
        portalMouseListenerRef.current = null;
      }
      if (portalObserverRef.current) {
        portalObserverRef.current.disconnect();
        portalObserverRef.current = null;
      }
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const removePortalTracker = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      removePortalTracker();
      const checkMouse = ev => {
        const target = ev.target as HTMLElement | null;
        if (!target) {
          return;
        }
        const overPanel = panelRef.current?.contains(target);
        const overPortal = target.closest("[data-radix-popper-content-wrapper]");
        const portalOpen = document.querySelector("[data-radix-popper-content-wrapper]");
        if (!overPanel && !overPortal && !portalOpen) {
          setExpanded(false);
          removePortalTracker();
        }
      };
      portalMouseListenerRef.current = checkMouse;
      document.addEventListener("mousemove", checkMouse);
      const observer = new MutationObserver(() => {
        if (document.querySelector("[data-radix-popper-content-wrapper]")) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          setExpanded(false);
        }
        removePortalTracker();
      });
      observer.observe(document.body, {
        childList: true
      });
      portalObserverRef.current = observer;
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const installPortalTracker = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = section => e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      setOptimisticSection(section);
      railRowNavigationRef.current = true;
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRailRowClick = t4;
  let t5;
  if ($[8] !== expanded || $[9] !== hasHoverPointer) {
    t5 = () => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
        return;
      }
      removePortalTracker();
      if (expanded) {
        return;
      }
      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          return;
        }
        setExpanded(true);
      }, RAIL_OPEN_DELAY_MS);
    };
    $[8] = expanded;
    $[9] = hasHoverPointer;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      clearOpenTimer();
      suppressExpandUntilPointerLeaves = false;
      removeSuppressClearListener();
      const related = e_0.relatedTarget;
      if (related instanceof HTMLElement && related.closest("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      if (document.querySelector("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      setExpanded(false);
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleMouseLeave = t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      removePortalTracker();
      clearOpenTimer();
    };
    t8 = [removePortalTracker, clearOpenTimer];
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== router.events) {
    t9 = () => {
      const handleRouteChangeStart = () => {
        const railRowNavigation = railRowNavigationRef.current;
        railRowNavigationRef.current = false;
        if (railRowNavigation && panelRef.current?.matches(":hover")) {
          armSuppressUntilPointerLeaves();
        }
        clearOpenTimer();
        removePortalTracker();
      };
      router.events.on("routeChangeStart", handleRouteChangeStart);
      return () => {
        router.events.off("routeChangeStart", handleRouteChangeStart);
      };
    };
    t10 = [router.events, removePortalTracker, clearOpenTimer];
    $[14] = router.events;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== router.events) {
    t11 = () => {
      const settle = () => {
        if (RAIL_HOVER_EXPAND) {
          setInstantClose(true);
          setExpanded(false);
        }
        setOptimisticSection(null);
      };
      router.events.on("routeChangeComplete", settle);
      router.events.on("routeChangeError", settle);
      return () => {
        router.events.off("routeChangeComplete", settle);
        router.events.off("routeChangeError", settle);
      };
    };
    t12 = [router.events];
    $[17] = router.events;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[20] !== instantClose) {
    t13 = () => {
      if (!instantClose) {
        return;
      }
      const id = requestAnimationFrame(() => setInstantClose(false));
      return () => cancelAnimationFrame(id);
    };
    t14 = [instantClose];
    $[20] = instantClose;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[23] !== expanded) {
    t15 = () => {
      pinnedExpanded = expanded;
    };
    t16 = [expanded];
    $[23] = expanded;
    $[24] = t15;
    $[25] = t16;
  } else {
    t15 = $[24];
    t16 = $[25];
  }
  useLayoutEffect(t15, t16);
  let t17;
  let t18;
  if ($[26] !== layoutWidth) {
    t17 = () => {
      document.documentElement.style.setProperty(RAIL_WIDTH_CSS_VAR, `${layoutWidth}px`);
    };
    t18 = [layoutWidth];
    $[26] = layoutWidth;
    $[27] = t17;
    $[28] = t18;
  } else {
    t17 = $[27];
    t18 = $[28];
  }
  useLayoutEffect(t17, t18);
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [];
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  useLayoutEffect(_temp2, t19);
  let t20;
  let t21;
  if ($[30] !== codesmithEnabled || $[31] !== router || $[32] !== sessionsNewHref) {
    t20 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleNewChat = e_1 => {
        if (e_1.altKey && e_1.shiftKey && !e_1.metaKey && !e_1.ctrlKey && e_1.code === "KeyN") {
          e_1.preventDefault();
          setOptimisticSection("codesmith");
          router.push(sessionsNewHref);
        }
      };
      document.addEventListener("keydown", handleNewChat);
      return () => document.removeEventListener("keydown", handleNewChat);
    };
    t21 = [codesmithEnabled, sessionsNewHref, router];
    $[30] = codesmithEnabled;
    $[31] = router;
    $[32] = sessionsNewHref;
    $[33] = t20;
    $[34] = t21;
  } else {
    t20 = $[33];
    t21 = $[34];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[35] !== codesmithAutomationsEnabled || $[36] !== currentOrg || $[37] !== router) {
    t22 = () => {
      if (!codesmithAutomationsEnabled) {
        return;
      }
      const handleAutomations = e_2 => {
        if (e_2.altKey && e_2.shiftKey && !e_2.metaKey && !e_2.ctrlKey && e_2.code === "KeyA") {
          e_2.preventDefault();
          const automationsPath = currentOrg ? `/${currentOrg}/automations` : "/automations";
          setOptimisticSection("codesmith");
          router.push(automationsPath);
        }
      };
      document.addEventListener("keydown", handleAutomations);
      return () => document.removeEventListener("keydown", handleAutomations);
    };
    t23 = [codesmithAutomationsEnabled, currentOrg, router];
    $[35] = codesmithAutomationsEnabled;
    $[36] = currentOrg;
    $[37] = router;
    $[38] = t22;
    $[39] = t23;
  } else {
    t22 = $[38];
    t23 = $[39];
  }
  useEffect(t22, t23);
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [];
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  useEffect(_temp4, t24);
  const railShortcutsActive = routeSection !== "codesmith" && optimisticSection !== "codesmith";
  let t25;
  let t26;
  if ($[41] !== codesmithEnabled || $[42] !== gPrefixActiveRef || $[43] !== homeHref || $[44] !== proofHref || $[45] !== railShortcutsActive || $[46] !== router || $[47] !== runsJobsHref || $[48] !== sessionsNewHref) {
    t25 = () => {
      if (!railShortcutsActive) {
        return;
      }
      const hrefs = {
        home: homeHref,
        actions: runsJobsHref,
        codesmith: codesmithEnabled ? sessionsNewHref : null,
        proof: codesmithEnabled ? proofHref : null
      };
      const handleRailShortcut = e_4 => {
        if (gPrefixActiveRef.current) {
          return;
        }
        const section_0 = railShortcutSection(e_4, lastTextEntryAt);
        const href = section_0 && hrefs[section_0];
        if (!section_0 || !href) {
          return;
        }
        e_4.preventDefault();
        setOptimisticSection(section_0);
        router.push(href);
      };
      document.addEventListener("keydown", handleRailShortcut);
      return () => document.removeEventListener("keydown", handleRailShortcut);
    };
    t26 = [railShortcutsActive, homeHref, runsJobsHref, codesmithEnabled, sessionsNewHref, proofHref, gPrefixActiveRef, router];
    $[41] = codesmithEnabled;
    $[42] = gPrefixActiveRef;
    $[43] = homeHref;
    $[44] = proofHref;
    $[45] = railShortcutsActive;
    $[46] = router;
    $[47] = runsJobsHref;
    $[48] = sessionsNewHref;
    $[49] = t25;
    $[50] = t26;
  } else {
    t25 = $[49];
    t26 = $[50];
  }
  useEffect(t25, t26);
  const t27 = currentOrg ?? undefined;
  let t28;
  if ($[51] !== globalOrgBlocked || $[52] !== t27) {
    t28 = <SidebarBlockedOrgBanner activeGithubOrgBlocked={globalOrgBlocked} currentOrg={t27} />;
    $[51] = globalOrgBlocked;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  const t29 = expanded ? "expanded" : "collapsed";
  const t30 = redesign && !expanded && RAIL_REDESIGN_CLASS;
  let t31;
  if ($[54] !== t30) {
    t31 = cn("group relative h-full shrink-0 flex flex-col overflow-hidden", "bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", t30);
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  const t32 = instantClose ? "none" : RAIL_TRANSITION_STYLE;
  let t33;
  if ($[56] !== layoutWidth || $[57] !== t32) {
    t33 = {
      width: layoutWidth,
      transition: t32
    };
    $[56] = layoutWidth;
    $[57] = t32;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  const t34 = expanded ? "Collapse sidebar" : "Expand sidebar";
  let t35;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setExpanded(_temp5);
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  const t36 = expanded ? "Collapse sidebar" : "Expand sidebar";
  const t37 = expanded ? "opacity-0" : "opacity-100";
  const t38 = instantClose ? "transition-none" : "[transition:opacity_100ms_ease-out]";
  let t39;
  if ($[60] !== t37 || $[61] !== t38) {
    t39 = cn("flex size-8 shrink-0 items-center justify-center", t37, t38);
    $[60] = t37;
    $[61] = t38;
    $[62] = t39;
  } else {
    t39 = $[62];
  }
  let t40;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div>;
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  if ($[64] !== t39) {
    t41 = <div className={t39}>{t40}</div>;
    $[64] = t39;
    $[65] = t41;
  } else {
    t41 = $[65];
  }
  let t42;
  if ($[66] !== expanded || $[67] !== instantClose) {
    t42 = <div aria-hidden={true} className="pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2"><BlacksmithWordmarkSVG expanded={expanded} instant={instantClose} className="h-4 w-auto origin-left scale-[1.0485] text-[#0c0c0de5] dark:text-blacksmith-yellow" /></div>;
    $[66] = expanded;
    $[67] = instantClose;
    $[68] = t42;
  } else {
    t42 = $[68];
  }
  let t43;
  if ($[69] !== t36 || $[70] !== t41 || $[71] !== t42) {
    t43 = <button type="button" onClick={t35} aria-label={t36} className="relative flex h-8 w-full items-center hover:opacity-70 transition-opacity cursor-pointer">{t41}{t42}</button>;
    $[69] = t36;
    $[70] = t41;
    $[71] = t42;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] !== t34 || $[74] !== t43) {
    t44 = <AppHeaderBar><CollapsedIconTooltip label={t34} enabled={true}>{t43}</CollapsedIconTooltip></AppHeaderBar>;
    $[73] = t34;
    $[74] = t43;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  const t45 = redesign ? undefined : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  let t46;
  if ($[76] !== expanded) {
    t46 = <GitHubOrgSelector expanded={expanded} />;
    $[76] = expanded;
    $[77] = t46;
  } else {
    t46 = $[77];
  }
  let t47;
  if ($[78] !== t45 || $[79] !== t46) {
    t47 = <div className={t45}>{t46}</div>;
    $[78] = t45;
    $[79] = t46;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  const t48 = !redesign && "gap-1 pt-1";
  let t49;
  if ($[81] !== t48) {
    t49 = cn("rail-rows flex flex-col", t48);
    $[81] = t48;
    $[82] = t49;
  } else {
    t49 = $[82];
  }
  const t50 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.home : undefined;
  const t51 = currentOrg ? true : null;
  const t52 = !!currentOrg;
  let t53;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = handleRailRowClick("home");
    $[83] = t53;
  } else {
    t53 = $[83];
  }
  const t54 = activeSection === "home" ? "page" : undefined;
  const t55 = activeSection === "home" && RAIL_ROW_ACTIVE;
  let t56;
  if ($[84] !== t55) {
    t56 = cn(RAIL_ROW, t55);
    $[84] = t55;
    $[85] = t56;
  } else {
    t56 = $[85];
  }
  let t57;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <RailRowContents icon={<House className="size-[18px]" />} label="Home" />;
    $[86] = t57;
  } else {
    t57 = $[86];
  }
  let t58;
  if ($[87] !== homeHref || $[88] !== t51 || $[89] !== t52 || $[90] !== t54 || $[91] !== t56) {
    t58 = <RoutePrefetchLink href={homeHref} prefetch={t51} prefetchOnIntent={t52} onClick={t53} aria-current={t54} className={t56}>{t57}</RoutePrefetchLink>;
    $[87] = homeHref;
    $[88] = t51;
    $[89] = t52;
    $[90] = t54;
    $[91] = t56;
    $[92] = t58;
  } else {
    t58 = $[92];
  }
  let t59;
  if ($[93] !== slim || $[94] !== t50 || $[95] !== t58) {
    t59 = <CollapsedIconTooltip label="Home" enabled={slim} shortcut={t50}>{t58}</CollapsedIconTooltip>;
    $[93] = slim;
    $[94] = t50;
    $[95] = t58;
    $[96] = t59;
  } else {
    t59 = $[96];
  }
  const t60 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.actions : undefined;
  const t61 = currentOrg ? true : null;
  const t62 = !!currentOrg;
  let t63;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = handleRailRowClick("actions");
    $[97] = t63;
  } else {
    t63 = $[97];
  }
  const t64 = activeSection === "actions" ? "page" : undefined;
  const t65 = activeSection === "actions" && RAIL_ROW_ACTIVE;
  let t66;
  if ($[98] !== t65) {
    t66 = cn(RAIL_ROW, t65);
    $[98] = t65;
    $[99] = t66;
  } else {
    t66 = $[99];
  }
  let t67;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <RailRowContents icon={<Github className="size-[18px]" />} label="GitHub Actions" />;
    $[100] = t67;
  } else {
    t67 = $[100];
  }
  let t68;
  if ($[101] !== runsJobsHref || $[102] !== t61 || $[103] !== t62 || $[104] !== t64 || $[105] !== t66) {
    t68 = <RoutePrefetchLink href={runsJobsHref} prefetch={t61} prefetchOnIntent={t62} onClick={t63} aria-current={t64} className={t66}>{t67}</RoutePrefetchLink>;
    $[101] = runsJobsHref;
    $[102] = t61;
    $[103] = t62;
    $[104] = t64;
    $[105] = t66;
    $[106] = t68;
  } else {
    t68 = $[106];
  }
  let t69;
  if ($[107] !== slim || $[108] !== t60 || $[109] !== t68) {
    t69 = <CollapsedIconTooltip label="GitHub Actions" enabled={slim} shortcut={t60}>{t68}</CollapsedIconTooltip>;
    $[107] = slim;
    $[108] = t60;
    $[109] = t68;
    $[110] = t69;
  } else {
    t69 = $[110];
  }
  let t70;
  if ($[111] !== activeSection || $[112] !== codesmithEnabled || $[113] !== codesmithRowHovered || $[114] !== currentOrg || $[115] !== railShortcutsActive || $[116] !== sessionsNewHref || $[117] !== slim) {
    t70 = codesmithEnabled && (() => {
      const codesmithRow = <RoutePrefetchLink href={sessionsNewHref} prefetchOnIntent={!!currentOrg} onMouseEnter={() => setCodesmithRowHovered(true)} onMouseLeave={() => setCodesmithRowHovered(false)} onClick={handleRailRowClick("codesmith")} aria-current={activeSection === "codesmith" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "codesmith" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<CodesmithLottieIcon play={codesmithRowHovered} />} label="[code]smith" /></RoutePrefetchLink>;
      return <Tooltip><TooltipTrigger asChild={true}>{codesmithRow}</TooltipTrigger>{slim && <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">[code]smith</span>{railShortcutsActive && <ShortcutKeys keys={[RAIL_SHORTCUT_LABELS.codesmith]} />}</TooltipContent>}</Tooltip>;
    })();
    $[111] = activeSection;
    $[112] = codesmithEnabled;
    $[113] = codesmithRowHovered;
    $[114] = currentOrg;
    $[115] = railShortcutsActive;
    $[116] = sessionsNewHref;
    $[117] = slim;
    $[118] = t70;
  } else {
    t70 = $[118];
  }
  let t71;
  if ($[119] !== activeSection || $[120] !== codesmithEnabled || $[121] !== currentOrg || $[122] !== proofHref || $[123] !== railShortcutsActive || $[124] !== slim) {
    t71 = codesmithEnabled && <CollapsedIconTooltip label="Proof" enabled={slim} shortcut={railShortcutsActive ? RAIL_SHORTCUT_LABELS.proof : undefined}><RoutePrefetchLink href={proofHref} prefetchOnIntent={!!currentOrg} onClick={handleRailRowClick("proof")} aria-current={activeSection === "proof" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "proof" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<ProofIconBold className="size-[18px]" />} label="Proof" /></RoutePrefetchLink></CollapsedIconTooltip>;
    $[119] = activeSection;
    $[120] = codesmithEnabled;
    $[121] = currentOrg;
    $[122] = proofHref;
    $[123] = railShortcutsActive;
    $[124] = slim;
    $[125] = t71;
  } else {
    t71 = $[125];
  }
  let t72;
  if ($[126] !== t49 || $[127] !== t59 || $[128] !== t69 || $[129] !== t70 || $[130] !== t71) {
    t72 = <nav className={t49} aria-label="Apps">{t59}{t69}{t70}{t71}</nav>;
    $[126] = t49;
    $[127] = t59;
    $[128] = t69;
    $[129] = t70;
    $[130] = t71;
    $[131] = t72;
  } else {
    t72 = $[131];
  }
  let t73;
  if ($[132] !== activeSection || $[133] !== expanded || $[134] !== redesign || $[135] !== slim) {
    t73 = !redesign && <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} />;
    $[132] = activeSection;
    $[133] = expanded;
    $[134] = redesign;
    $[135] = slim;
    $[136] = t73;
  } else {
    t73 = $[136];
  }
  let t74;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <div className="flex-1" />;
    $[137] = t74;
  } else {
    t74 = $[137];
  }
  let t75;
  if ($[138] !== activeSection || $[139] !== expanded || $[140] !== redesign || $[141] !== slim) {
    t75 = redesign ? <SidebarAdminRow expanded={expanded} active={activeSection === "admin"} onClick={handleRailRowClick("admin")} hideLabelWhenCollapsed={slim} divider={false} /> : null;
    $[138] = activeSection;
    $[139] = expanded;
    $[140] = redesign;
    $[141] = slim;
    $[142] = t75;
  } else {
    t75 = $[142];
  }
  const t76 = currentOrg ?? undefined;
  const t77 = activeApp === "codesmith";
  let t78;
  if ($[143] !== currentOrg || $[144] !== t77) {
    t78 = settingsPagePath(currentOrg, t77);
    $[143] = currentOrg;
    $[144] = t77;
    $[145] = t78;
  } else {
    t78 = $[145];
  }
  let t79;
  if ($[146] !== activeSection || $[147] !== expanded || $[148] !== redesign || $[149] !== slim || $[150] !== t75 || $[151] !== t76 || $[152] !== t78) {
    t79 = <SidebarGlobalFooter leading={t75} expanded={expanded} currentOrg={t76} settingsHref={t78} activeSection={activeSection} onSectionClick={handleRailRowClick} className="pb-2" hideLabelWhenCollapsed={slim} statusAsHoverCard={slim} redesign={redesign} />;
    $[146] = activeSection;
    $[147] = expanded;
    $[148] = redesign;
    $[149] = slim;
    $[150] = t75;
    $[151] = t76;
    $[152] = t78;
    $[153] = t79;
  } else {
    t79 = $[153];
  }
  let t80;
  if ($[154] !== t44 || $[155] !== t47 || $[156] !== t72 || $[157] !== t73 || $[158] !== t79) {
    t80 = <div className="flex h-full w-full flex-col px-2">{t44}{t47}{t72}{t73}{t74}{t79}</div>;
    $[154] = t44;
    $[155] = t47;
    $[156] = t72;
    $[157] = t73;
    $[158] = t79;
    $[159] = t80;
  } else {
    t80 = $[159];
  }
  let t81;
  if ($[160] !== handleMouseEnter || $[161] !== t29 || $[162] !== t31 || $[163] !== t33 || $[164] !== t80) {
    t81 = <div ref={panelRef} data-slot="app-switcher-rail" data-state={t29} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={t31} style={t33}>{t80}</div>;
    $[160] = handleMouseEnter;
    $[161] = t29;
    $[162] = t31;
    $[163] = t33;
    $[164] = t80;
    $[165] = t81;
  } else {
    t81 = $[165];
  }
  let t82;
  if ($[166] !== t28 || $[167] !== t81) {
    t82 = <>{t28}{t81}</>;
    $[166] = t28;
    $[167] = t81;
    $[168] = t82;
  } else {
    t82 = $[168];
  }
  return t82;
}
function _temp5(prev) {
  return !prev;
}
function _temp4() {
  const noteTextEntry = _temp3;
  document.addEventListener("keydown", noteTextEntry, true);
  return () => document.removeEventListener("keydown", noteTextEntry, true);
}
function _temp3(e_3) {
  if (isTypingTarget(e_3.target)) {
    lastTextEntryAt = e_3.timeStamp;
  }
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.documentElement.style.removeProperty(RAIL_WIDTH_CSS_VAR);
}